import {
  APIEndpoints,
  API_MS_ASSETS_STATS,
  API_MS_ASSET_HISTORY,
  API_MS_ASSET_REQUESTS,
  API_MS_ASSET_REQUEST_STATS,
  API_MS_CANCEL_ASSET_REQUEST,
  API_MS_GET_ALL_ASSETS,
  API_MS_GET_ALL_ASSET_BRANDS,
  API_MS_GET_ALL_ASSET_TYPES,
  API_MS_GET_ASSET_REQUEST_DETAILS_BY_ID,
  API_MS_GET_CONSUMABLES_BY_CATEGORY,
  API_MS_GET_CONSUMABLE_ASSET_CATEGORY,
  API_MS_REPORT_ASSET_ISSUE,
  API_MS_REQUEST_ASSET_REPLACEMENT,
} from "@/constants/apiEndpoints";
import type {
  IAssetBrand,
  IAssetStats,
  IConsumableAssetCategory,
  IMSCancelAssetRequestPayload,
  ISingleAssetType,
  ISingleConsumableAsset,
  ISingleUserAssetRequest,
  TCreateAssetResponse,
  TMSGetAllAssetsRequest,
  TMSGetAllAssetsResponse,
} from "@/types/(my-space)/assets.interface";
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import type {
  IAssetRequestData,
  IAssetRequestStats,
} from "@/types/(company)/assets.interface";

export const assetsApi = createApi({
  reducerPath: "assetsApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  tagTypes: [
    "msGetAllAssets",
    "msGetAllAssetRequests",
    "msAssetRequestStats",
  ],
  endpoints: (builder) => ({
    msGetAllAssets: builder.query<
      TMSGetAllAssetsResponse,
      TMSGetAllAssetsRequest
    >({
      query: (params) => ({
        url: API_MS_GET_ALL_ASSETS,
        params,
      }),
      providesTags: ["msGetAllAssets"],
    }),
    getAssetsStats: builder.query<IApiResponse<IAssetStats>, void>({
      query: () => ({
        url: API_MS_ASSETS_STATS,
      }),
    }),
    msGetAllAssetRequests: builder.query<
      IApiResponse<ISingleUserAssetRequest[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: API_MS_ASSET_REQUESTS,
        params,
      }),
      providesTags: ["msGetAllAssetRequests"],
    }),
    getMsAssetRequestStats: builder.query<
      IApiResponse<IAssetRequestStats>,
      void
    >({
      query: () => ({
        url: API_MS_ASSET_REQUEST_STATS,
        method: "GET",
      }),
      providesTags: ["msAssetRequestStats"],
    }),
    msCancelAssetRequest: builder.mutation<
      TCreateAssetResponse,
      IMSCancelAssetRequestPayload
    >({
      query: (body) => ({
        url: API_MS_CANCEL_ASSET_REQUEST(body.assetRequestId),
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        "msGetAllAssetRequests",
        "msGetAllAssets",
        "msAssetRequestStats",
      ],
    }),
    msRequestAssetReplacement: builder.mutation<
      IApiResponse<void>,
      {
        assetId: number;
        payload: {
          reason: string;
          requiredDate?: string;
          description: string;
        };
      }
    >({
      query: ({ assetId, payload }) => ({
        url: API_MS_REQUEST_ASSET_REPLACEMENT(assetId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["msGetAllAssetRequests", "msGetAllAssets"],
    }),
    msReportAssetIssue: builder.mutation<
      IApiResponse<void>,
      { assetId: number; payload: FormData }
    >({
      query: ({ assetId, payload }) => ({
        url: API_MS_REPORT_ASSET_ISSUE(assetId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["msGetAllAssets", "msGetAllAssetRequests"],
    }),
    msGetAllAssetTypes: builder.query<IApiResponse<ISingleAssetType[]>, void>({
      query: () => ({
        url: API_MS_GET_ALL_ASSET_TYPES,
      }),
    }),
    msGetAllAssetType: builder.infiniteQuery<
      IApiPaginatedResponse<ISingleAssetType[]>,
      { limit: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          const { page, lastPage: totalPages } = lastPage.meta;
          return page < totalPages ? page + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: API_MS_GET_ALL_ASSET_TYPES,
        params: {
          limit: queryArg.limit,
          page: pageParam,
        },
      }),
    }),
    msCreateAssetRequest: builder.mutation<IApiResponse<void>, FormData>({
      query: (body) => ({
        url: API_MS_ASSET_REQUESTS,
        method: "POST",
        body,
      }),
      invalidatesTags: ["msGetAllAssetRequests", "msAssetRequestStats"],
    }),
    msGetConsumableAssetsByCategory: builder.query<
      IApiResponse<ISingleConsumableAsset[]>,
      { category: string }
    >({
      query: ({ category }) => ({
        url: API_MS_GET_CONSUMABLES_BY_CATEGORY(category),
      }),
    }),
    msGetAllAssetBrands: builder.query<
      IApiPaginatedResponse<Array<IAssetBrand>>,
      void
    >({
      query: () => ({
        url: API_MS_GET_ALL_ASSET_BRANDS,
      }),
    }),
    // For infinite scrolling/load-more functionality
    msGetAssetBrands: builder.infiniteQuery<
      IApiPaginatedResponse<Array<IAssetBrand>>,
      { limit: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          const { page, lastPage: totalPages } = lastPage.meta;
          return page < totalPages ? page + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: API_MS_GET_ALL_ASSET_BRANDS,
        params: {
          limit: queryArg.limit,
          page: pageParam,
        },
      }),
    }),
    msGetConsumableAssetCategory: builder.query<
      IApiResponse<IConsumableAssetCategory[]>,
      void
    >({
      query: () => ({
        url: API_MS_GET_CONSUMABLE_ASSET_CATEGORY,
      }),
    }),
    msGetAssetRequestDetailsById: builder.query<
      IApiResponse<IAssetRequestData>,
      { requestId: number }
    >({
      query: ({ requestId }) => ({
        url: API_MS_GET_ASSET_REQUEST_DETAILS_BY_ID(requestId),
        method: "GET",
      }),
    }),
    msGetAssetRequestById: builder.query<
      IApiResponse<IAssetRequestData>,
      {
        requestId: number;
      }
    >({
      query: ({ requestId }) => ({
        url: APIEndpoints.MySpace.Assets.GetAssetRequestById(requestId),
      }),
    }),
    msGetMyAssetHistory: builder.query<IApiResponse<any[]>, void>({
      query: () => ({ url: API_MS_ASSET_HISTORY }),
    }),
  }),
});

export const {
  useMsGetAllAssetsQuery,
  useGetAssetsStatsQuery,
  useMsGetAllAssetRequestsQuery,
  useGetMsAssetRequestStatsQuery,
  useMsCancelAssetRequestMutation,
  useMsRequestAssetReplacementMutation,
  useMsReportAssetIssueMutation,
  useMsCreateAssetRequestMutation,
  useMsGetAllAssetBrandsQuery,
  useLazyMsGetConsumableAssetsByCategoryQuery,
  useLazyMsGetAllAssetTypesQuery,
  useMsGetAllAssetTypesQuery,
  useLazyMsGetAllAssetBrandsQuery,
  useMsGetAssetBrandsInfiniteQuery,
  useMsGetAllAssetTypeInfiniteQuery,
  useMsGetConsumableAssetCategoryQuery,
  useMsGetAssetRequestByIdQuery,
  useMsGetAssetRequestDetailsByIdQuery,
  useMsGetMyAssetHistoryQuery,
} = assetsApi;
